/**
 * Dialog per correggere i DATI ANAGRAFICI di una fattura in bozza.
 *
 * Perche' esiste: fino al 2026-09-14 il dettaglio fattura non offriva NESSUN
 * percorso di modifica. L'endpoint PATCH /api/v1/fatture/{id} esisteva gia' da
 * BL-085 ma nessun componente lo chiamava: chi voleva correggere una bozza
 * trovava come unica azione plausibile "Revoca bozza", che e' una cancellazione
 * irreversibile, e distruggeva il documento. E' successo davvero, su una fattura
 * diretta (senza ordine di origine) che quindi non era nemmeno rigenerabile.
 *
 * Segue il pattern di ordini/.../CambiaIntestatarioDialog.tsx: Dialog di
 * @floraltech/ui (Radix: focus trap, Esc, aria-modal), PATCH su documento in
 * bozza, onSaved che aggiorna il chiamante senza rimontare la pagina.
 *
 * PERIMETRO, e il perche' di ogni esclusione:
 * - Gli IMPORTI non ci sono perche' il backend non li accetta, deliberatamente:
 *   i totali di testata devono discendere dalle righe (controllo SDI 00422,
 *   bloccante) e un valore libero produrrebbe un documento aritmeticamente
 *   falso (v. docstring di FatturaPatch in api/v1/fatture.py).
 * - Le RIGHE non ci sono perche' non esiste alcun endpoint che le modifichi:
 *   su /{id}/righe c'e' solo la GET.
 * - Il CENTRO DI COSTO non e' qui perche' e' gia' modificabile dal dettaglio,
 *   con il suo Combobox e il suo endpoint dedicato: duplicarlo darebbe due
 *   comandi per lo stesso dato.
 * - Il TIPO DOCUMENTO non e' qui perche' l'unico elenco disponibile e' un array
 *   cablato dentro FatturaForm: esporlo significherebbe duplicare un
 *   hardcoding. Serve prima la tabella di lookup (proposta a Walter).
 * Cio' che non si puo' correggere resta VISIBILE e disabilitato, con la ragione
 * accanto: un campo nascosto fa credere che il dato non esista.
 */

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@floraltech/ui';
import React, { useEffect, useState } from 'react';

/** Campi che il PATCH accetta e che questo dialog espone. */
export interface AnagraficaFattura {
  data: string;
  cedente_denominazione: string | null;
  cedente_piva: string | null;
  cessionario_denominazione: string | null;
  cessionario_piva: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fatturaId: string;
  year: string;
  /** Valori attuali, usati per precompilare i campi a ogni apertura. */
  valori: AnagraficaFattura;
  /** Mostrati in sola lettura: dicono all'utente dove finisce il correggibile. */
  soloLettura: {
    cessionario_codice_sdi: string | null;
    cessionario_cf: string | null;
    importo_totale: number | null;
  };
  authHeaders: () => Record<string, string>;
  /** Riceve i soli campi salvati: il chiamante li fonde nel suo stato. La
   *  risposta del PATCH ha uno schema piu' snello del GET dettaglio, quindi
   *  non basta a se stessa per sostituire la fattura in pagina. */
  onSaved: (salvati: AnagraficaFattura) => void;
}

export function ModificaAnagraficaFatturaDialog({
  open,
  onOpenChange,
  fatturaId,
  year,
  valori,
  soloLettura,
  authHeaders,
  onSaved,
}: Props) {
  const [form, setForm] = useState<AnagraficaFattura>(valori);
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  // Ricarica i valori correnti a ogni apertura: evita di mostrare una modifica
  // abbandonata in precedenza come se fosse il dato salvato.
  useEffect(() => {
    if (open) {
      setForm(valori);
      setErrore(null);
      setSaving(false);
    }
    // `valori` e' volutamente fuori dalle dipendenze: conta il valore al
    // momento dell'apertura, non ogni suo cambiamento mentre il dialog e' su.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (campo: keyof AnagraficaFattura, v: string) =>
    setForm((prev) => ({ ...prev, [campo]: v }));

  const handleSalva = async () => {
    if (!form.data) {
      setErrore('La data del documento e\' obbligatoria.');
      return;
    }
    setSaving(true);
    setErrore(null);
    try {
      const res = await fetch(`/api/v1/fatture/${fatturaId}?year=${year}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data_documento: form.data,
          cedente_denominazione: form.cedente_denominazione,
          cedente_piva: form.cedente_piva,
          cessionario_denominazione: form.cessionario_denominazione,
          cessionario_piva: form.cessionario_piva,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        // 409 = la fattura non e' piu' in bozza. Non e' un errore dell'utente e
        // i suoi dati non vanno persi: resta tutto compilato.
        if (res.status === 409) {
          setErrore(
            'Questa fattura non e\' piu\' in bozza, quindi non e\' piu\' modificabile. ' +
              'Ricarica la pagina per vedere lo stato aggiornato: i valori che hai scritto qui restano finche\' non chiudi.'
          );
        } else {
          setErrore(
            typeof b?.detail === 'string' ? b.detail : `Salvataggio fallito (errore ${res.status}).`
          );
        }
        return;
      }
      onSaved(form);
      onOpenChange(false);
    } catch {
      setErrore('Errore di rete: verifica la connessione e riprova. I dati che hai scritto restano qui.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Modifica dati fattura</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Correzione rapida dei soli dati anagrafici, senza lasciare la pagina. Per
          righe, importi e dati di pagamento chiudi e usa &quot;Modifica righe e
          importi&quot;.
        </p>

        <div className="space-y-3">
          <div>
            <Label htmlFor="mod-data">Data documento</Label>
            <Input
              id="mod-data"
              type="date"
              value={form.data ?? ''}
              onChange={(e) => set('data', e.target.value)}
              disabled={saving}
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="mod-cess-den">Cliente - denominazione</Label>
              <Input
                id="mod-cess-den"
                value={form.cessionario_denominazione ?? ''}
                onChange={(e) => set('cessionario_denominazione', e.target.value)}
                disabled={saving}
              />
            </div>
            <div>
              <Label htmlFor="mod-cess-piva">Cliente - P.IVA</Label>
              <Input
                id="mod-cess-piva"
                value={form.cessionario_piva ?? ''}
                onChange={(e) => set('cessionario_piva', e.target.value)}
                disabled={saving}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="mod-ced-den">Cedente - denominazione</Label>
              <Input
                id="mod-ced-den"
                value={form.cedente_denominazione ?? ''}
                onChange={(e) => set('cedente_denominazione', e.target.value)}
                disabled={saving}
              />
            </div>
            <div>
              <Label htmlFor="mod-ced-piva">Cedente - P.IVA</Label>
              <Input
                id="mod-ced-piva"
                value={form.cedente_piva ?? ''}
                onChange={(e) => set('cedente_piva', e.target.value)}
                disabled={saving}
              />
            </div>
          </div>

          {/* Il confine del correggibile, mostrato dove lo si incontra. */}
          <div className="rounded-md border border-dashed p-3 space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              Non modificabili da qui
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="mod-ro-sdi" className="text-muted-foreground">
                  Codice SDI cliente
                </Label>
                <Input
                  id="mod-ro-sdi"
                  value={soloLettura.cessionario_codice_sdi ?? ''}
                  disabled
                />
              </div>
              <div>
                <Label htmlFor="mod-ro-cf" className="text-muted-foreground">
                  Codice fiscale cliente
                </Label>
                <Input id="mod-ro-cf" value={soloLettura.cessionario_cf ?? ''} disabled />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Codice SDI e codice fiscale non sono ancora correggibili da nessuna
              schermata. Righe, importi e dati di pagamento si correggono con
              &quot;Modifica righe e importi&quot; sul dettaglio.
            </p>
          </div>

          {errore && <p className="text-sm text-destructive">{errore}</p>}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Annulla
          </Button>
          <Button type="button" onClick={() => void handleSalva()} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva modifiche'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ModificaAnagraficaFatturaDialog;
